import styled, { keyframes } from 'styled-components';

/* Mixins */
import {
  WebFontHeader2Bold,
  WebFontHeader4,
  WebFontHeader5,
  WebFontBody1,
  WebFontBody2,
  WebFontTitle2,
  WebFontCallout1,
  WebFontCalloutSemibold1,
  BoxSizingSelfAndChildren,
  FlexRow,
  FlexDirectionColumn,
  FlexCenterChildren,
  FlexCenterChildrenColumn,
  InvisibleHorizontalScrollBar,
} from 'styles/Mixins';

/* Utils */
import { mediaQuery, assignIfDefined } from 'styles/Utils';

/* Assets */
import HeroImageDesktop from 'assets/HeroImageDesktop.jpg';
import HeroImageMobile from 'assets/HeroImageMobile.jpg';

export const MOBILE_HEADER_HEIGHT = 60;
export const DESKTOP_HEADER_HEIGHT = 80;
export const HORIZONTAL_PADDING = '8%';

export const Wrapper = styled.div`
  ${FlexDirectionColumn};
  ${WebFontBody1};
  align-items: center;
  ${BoxSizingSelfAndChildren};
  color: ${({ theme }) => theme.Grey1};
`;

export const Header = styled.div<{ isFixed?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${({ theme }) => theme.white};
  z-index: ${({ theme }) => theme.zIndexBase1};
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  ${({ isFixed }) =>
    isFixed &&
    `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
  `}

  ${mediaQuery.mobile`
    padding: 0 4%;
    height: ${MOBILE_HEADER_HEIGHT}px;
  `}
  ${mediaQuery.desktop`
    padding: 0 ${HORIZONTAL_PADDING};
    height: ${DESKTOP_HEADER_HEIGHT}px;
  `}
`;

export const RightHeader = styled.div`
  display: flex;
  align-items: center;
  margin-left: 12px;
`;

export const LinksContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-right: 1vw;
  ${mediaQuery.mobile`
    display: none;
  `}
`;

export const ScrollLink = styled.div<{ isSelected?: boolean }>`
  ${WebFontCalloutSemibold1};
  margin: 0 1vw;
  cursor: pointer;
  color: ${({ theme, isSelected }) =>
    isSelected ? theme.darkCobalt : theme.Grey1};
`;

export const HeroImage = styled.div`
  width: 100%;
  background-size: cover;
  background-position: center;
  ${FlexCenterChildren};
  ${mediaQuery.mobile`
    height: calc(100vh - ${MOBILE_HEADER_HEIGHT}px - 8vh);
    margin-top: ${MOBILE_HEADER_HEIGHT}px;
    background-image: linear-gradient(to bottom, rgba(2, 39, 134, 0) -2%, rgb(2, 39, 134) 98.42%), url(${HeroImageMobile});
    align-items: flex-end;
  `}
  ${mediaQuery.desktop`
    height: calc(100vh - ${DESKTOP_HEADER_HEIGHT}px);
    margin-top: ${DESKTOP_HEADER_HEIGHT}px;
    background-image: linear-gradient(to bottom, rgba(2, 39, 134, 0) -2%, rgb(2, 39, 134) 98.42%), url(${HeroImageDesktop});
    justify-content: flex-start;
  `}
`;

export const HeroImageContentContainer = styled.div`
  ${FlexCenterChildrenColumn};
  color: ${({ theme }) => theme.white};
  align-items: stretch;
  ${mediaQuery.mobile`
    margin: 0 ${HORIZONTAL_PADDING} -24px;
  `}
  ${mediaQuery.desktop`
    width: 40%;
    min-width: 600px;
    margin: 0 ${HORIZONTAL_PADDING};
  `}
`;

export const HeroImageTitle = styled.h1`
  ${WebFontHeader2Bold};
  line-height: 1.2;
  ${mediaQuery.desktop`
    font-size: 72px;
  `}
`;

export const HeroImageSubtitle = styled.h2`
  line-height: 1.5;
  ${mediaQuery.mobile`
    ${WebFontBody1};
    margin: 12px 0;
    letter-spacing: 0.005em;
  `}
  ${mediaQuery.desktop`
    ${WebFontHeader5};
    margin: 40px 0;
    letter-spacing: 0.01em;
  `}
`;

export const HeroImageButtonsContainer = styled.div`
  ${mediaQuery.mobile`
    ${FlexDirectionColumn};
  `}
  ${mediaQuery.desktop`
    ${FlexRow};
    flex-direction: row-reverse;
  `}
`;

export const HeroImageCTAContainer = styled.div`
  flex: 1;
  display: flex;

  ${mediaQuery.mobile`
    flex-direction: column;
    align-items: stretch;
  `}
  ${mediaQuery.desktop`
    align-items: center;
  `}
`;

export const HeroImageCTAButtonWrapper = styled.div`
  flex: 1;
`;

export const HeroImageWatchDemoButton = styled.div`
  ${FlexRow};
  align-items: center;
  cursor: pointer;

  ${mediaQuery.mobile`
    margin-bottom: 24px;
  `}
  ${mediaQuery.desktop`
    margin-left: 32px;
  `}
`;

export const HeroImageButtonText = styled.div`
  ${mediaQuery.mobile`
    ${WebFontCallout1};
  `}
  ${mediaQuery.desktop`
    ${WebFontHeader5};
  `}
`;

export const Section = styled.div<{ backgroundColor?: string }>`
  ${FlexCenterChildrenColumn};
  width: 100%;
  background-color: ${({ backgroundColor, theme }) =>
    backgroundColor || theme.white};

  ${mediaQuery.mobile`
    padding: 64px ${HORIZONTAL_PADDING};
  `}
  ${mediaQuery.desktop`
    padding: 128px ${HORIZONTAL_PADDING};
  `}
`;

export const SectionTitle = styled.h2<{ color?: string; margin?: string }>`
  ${WebFontHeader2Bold};
  color: ${({ color, theme }) => color || theme.Grey1};
  word-break: break-word;
  ${assignIfDefined('margin')};
`;

export const SectionSubTitle = styled.h3<{
  color?: string;
  bold?: boolean;
  margin?: string;
}>`
  ${({ bold }) => (bold ? WebFontHeader5 : WebFontTitle2)};
  line-height: 1.5;
  color: ${({ color, theme }) => color || theme.Grey1};
  ${assignIfDefined('margin')};
`;

export const BoldText = styled.div<{ margin?: string }>`
  ${WebFontCallout1};
  ${assignIfDefined('margin')};
`;

export const BodyText = styled.div<{ color?: string; margin?: string }>`
  ${WebFontBody2};
  ${assignIfDefined('margin')};
  line-height: 1.5;
  color: ${({ color, theme }) => color || theme.Grey1};
`;

export const AnimationContainer = styled.div`
  margin-right: 16px;
  width: 32px;
  min-width: 32px;
`;

export const SectionBody = styled.div`
  ${mediaQuery.mobile`
    ${FlexDirectionColumn};
  `}
  ${mediaQuery.desktop`
    ${FlexRow};
  `}
`;

export const ImageContainer = styled.img<{ roundCorner?: boolean }>`
  width: 100%;
  ${({ roundCorner }) => roundCorner && 'border-radius: 8px'};
`;

export const ColumnContainer = styled.div`
  ${FlexDirectionColumn};
  flex: 1;
  ${mediaQuery.mobile`
    margin: 20px 0;
  `}
  ${mediaQuery.desktop`
    margin: 0 16px;
  `}
`;

export const RowContainer = styled.div`
  ${FlexRow};
  flex: 1;
  align-items: center;
`;

export const VideoSection = styled.div`
  ${FlexDirectionColumn};
  align-items: stretch;
  justify-content: space-between;
  width: 100%;
  background-color: ${({ theme }) => theme.Blue1};

  ${mediaQuery.desktop`
    flex-direction: row-reverse;
    padding: 128px ${HORIZONTAL_PADDING};
  `}
`;

export const VideoTextContainer = styled.div`
  ${FlexDirectionColumn};
  flex: 1;

  ${mediaQuery.mobile`
    padding: 64px ${HORIZONTAL_PADDING};
  `}
  ${mediaQuery.desktop`
    padding-right: 40px;
  `}
`;

export const VideoThumbnailContainer = styled.div`
  ${FlexCenterChildren};
  background-color: ${({ theme }) => theme.palettesGrey100};
  cursor: pointer;
  position: relative;

  ${mediaQuery.mobile`
    width: 100%;
    padding: 10vh 4vw;
  `}
  ${mediaQuery.desktop`
    width: 40vw;
    padding: 12vh 4vw;
    border-radius: 16px;
  `}
`;

export const VideoThumbnailTitle = styled.div`
  ${FlexDirectionColumn};
  align-items: center;
  flex: 2;
  margin: 0 20px;
`;

export const VideoThumbnailText = styled.div`
  line-height: 1.5;
  text-align: center;
  color: ${({ theme }) => theme.pickupGreen};
  margin-top: 12px;

  ${mediaQuery.mobile`
    ${WebFontHeader5};
  `}
  ${mediaQuery.desktop`
    ${WebFontHeader4};
  `}
`;

export const TestimonialsContainer = styled.div`
  ${FlexRow};
  width: 100%;

  ${mediaQuery.mobile`
    ${InvisibleHorizontalScrollBar};
  `}
`;

export const TestimonialsWrapper = styled.div`
  ${FlexRow};
  padding: 8px ${HORIZONTAL_PADDING};

  ${mediaQuery.mobile`
    padding-bottom: 64px;
  `}
  ${mediaQuery.desktop`
    padding-bottom: 128px;
  `}
`;

export const TestimonialCard = styled.div`
  ${FlexDirectionColumn};
  flex: 1;
  min-width: 240px;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  :not(:last-child) {
    margin-right: 16px;
  }
`;

export const AboutWishSection = styled.div`
  ${FlexRow};
  justify-content: space-between;
  align-items: center;
  width: 100%;

  ${mediaQuery.mobile`
    flex-direction: column-reverse;
  `}
`;

export const WishAppImageWrapper = styled.div`
  ${mediaQuery.mobile`
    max-height: 32vh;
    overflow: hidden;
    margin-bottom: -64px;
  `}
  ${mediaQuery.desktop`
    max-width: 25%;
    margin: 0 6%;
  `}
`;

export const AboutWishStats = styled.div`
  ${mediaQuery.mobile`
    ${FlexDirectionColumn};
  `}
  ${mediaQuery.desktop`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    column-gap: 24px;
  `}
`;

const FadeInLeft = keyframes`
  0% {
    opacity: 0;
    transform: translateX(20px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
`;

export const CallWrapper = styled.div`
  ${FlexDirectionColumn};
  align-items: flex-end;
  position: fixed;
  bottom: 3vh;
  right: 2vw;
  animation: ${FadeInLeft} 0.2s ease-in-out;
`;

export const CallTooltip = styled.div`
  ${FlexRow};
  color: ${({ theme }) => theme.white};
  background-color: ${({ theme }) => theme.palettesGrey800};
  padding: 16px;
  margin-bottom: 12px;
  border-radius: 4px;
  position: relative;

  &:after {
    border: solid transparent;
    border-width: 10px;
    content: '';
    height: 0;
    width: 0;
    position: absolute;
    top: 100%;
    right: 10%;
    border-top-color: ${({ theme }) => theme.palettesGrey800};
  }
`;

export const CallTextWrapper = styled.div``;

export const CloseIconWrapper = styled.div`
  margin-left: 12px;
  cursor: pointer;
`;

export const CallText = styled.div`
  ${WebFontBody2};
  line-height: 20px;
`;

export const PhoneIconWrapper = styled.div`
  ${FlexCenterChildren};
  width: 48px;
  height: 48px;
  border-radius: 100%;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  cursor: pointer;
  background-color: ${({ theme }) => theme.darkCobalt};
`;

export const SignupButtonContainer = styled.div`
  flex-direction: row;
  flex: 1;
  align-items: center;
  background-color: ${({ theme }) => theme.white};
  border-radius: 2px;
  color: ${({ theme }) => theme.Grey1};
  padding: 16px;
  ${mediaQuery.mobile`
    margin-bottom: 24px;
  `}
`;

export const SignupBonus = styled.div`
  ${FlexRow};
  align-items: center;
  margin-bottom: 16px;
`;

export const SignupIncentiveCaption = styled.div`
  border-radius: 2px;
  ${mediaQuery.mobile`
    ${WebFontHeader5};
  `}
  ${mediaQuery.desktop`
    ${WebFontHeader5};
  `}
`;
